/**
 * Polls tile generation status for orthophotos that are in 'generating' state.
 * Updates the project store when tiles become ready.
 *
 * Uses a stable tilesetId→status map to avoid restarting the polling interval
 * every time the orthophotos array reference changes (which happens on any
 * unrelated re-render). Only restarts when the set of 'generating' tilesets
 * actually changes.
 *
 * ALSO polls orthos that have a tilesetId but an UNKNOWN tileStatus: the
 * poller's own ready-flip is an updateOrthophoto whose entity PATCH can lose a
 * _version race (412 → conflict handler replaces the local ortho with the
 * server copy, wiping tileStatus). Without re-polling those, the ortho is
 * stuck rendering the low-res preview with no HD badge even though its tiles
 * are ready server-side. Including the unknown-status orthos makes the flip
 * self-healing: it retries every poll until the update sticks.
 */

import { useEffect, useRef } from 'react';
import { useProjectStore } from '../stores';
import { getTilesetStatus } from '../services/tileApi';
import { API_BASE } from '../services/apiFetch';
import { resolveSignedUrl } from '../services/signedAssetUrl';
import type { Orthophoto } from '../stores/projectStore';

const POLL_INTERVAL = 5000; // 5 seconds

// Unknown-status orthos are only RE-CHECKS (self-heal of a lost ready-flip), so
// a permanently-failing check (e.g. a read-only viewer's 403 on the status
// endpoint) must not retry forever. 'generating' orthos are NOT capped — that's
// live tracking of an active job. Keyed by tilesetId, session-lifetime.
const MAX_UNKNOWN_STATUS_FAILURES = 12;
const unknownStatusFailures = new Map<string, number>();

// Failed-regen rollback retries (keyed by the FAILED tileset id): a transient
// failure of the prior-status GET must not abandon the rollback after one shot
// — the ortho stays 'generating' and the next poll retries. Capped so a
// permanently-failing GET eventually falls through to the plain error flip.
const MAX_ROLLBACK_FAILURES = 5;
const rollbackFailures = new Map<string, number>();

/** Orthos whose tile state must be (re)resolved from the server. */
function needsPolling(o: Orthophoto): boolean {
  if (!o.tilesetId) return false;
  if (o.tileStatus === 'generating') return true;
  return o.tileStatus == null &&
    (unknownStatusFailures.get(o.tilesetId) ?? 0) < MAX_UNKNOWN_STATUS_FAILURES;
}

/**
 * Large uploads skip the synchronous Lambda preview and carry a data:/blob:
 * placeholder as their url; the COG converter writes an overview-derived
 * preview to the deterministic legacy key during ingest. Probe it (it's absent
 * on the legacy-Lambda rollback path and for pre-preview converter builds) and
 * return the adoptable URL, or null to keep the placeholder.
 */
async function probeConverterPreview(siteId: string, orthoId: string): Promise<string | null> {
  const previewUrl = `${API_BASE}/api/v2/assets/sites/${siteId}/orthophoto_preview/${orthoId}_preview.png`;
  try {
    const signed = await resolveSignedUrl(previewUrl);
    const resp = await fetch(signed);
    try { await resp.body?.cancel(); } catch { /* body may be locked/absent */ }
    return resp.ok ? previewUrl : null;
  } catch {
    return null;
  }
}

export default function useTileStatusPoller() {
  const siteId = useProjectStore((s) => s.meta.siteId);
  const orthophotos = useProjectStore((s) => s.data.orthophotos);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Track which tilesetIds we are actively polling so we don't restart
  // the interval when the orthophotos array ref changes but the pending
  // set hasn't changed.
  const pollingIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!siteId) {
      // No site — clear any leftover interval
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      pollingIdsRef.current.clear();
      return;
    }

    // Compute which tilesetIds need polling ('generating' + unknown-status)
    const pendingOrthos = orthophotos.filter(needsPolling);
    const pendingIds = new Set(pendingOrthos.map((o) => o.tilesetId!));

    // Check if the set of pending tilesetIds actually changed
    const prev = pollingIdsRef.current;
    const sameSet =
      pendingIds.size === prev.size &&
      [...pendingIds].every((id) => prev.has(id));

    if (pendingIds.size === 0) {
      // Nothing to poll — clean up
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      pollingIdsRef.current.clear();
      return;
    }

    if (sameSet && timerRef.current) {
      // Same tilesets still generating, interval already running — no-op
      return;
    }

    // Pending set changed — restart polling with fresh list
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    pollingIdsRef.current = pendingIds;

    console.log(`[TilePoller] Polling ${pendingOrthos.length} generating tileset(s):`, [...pendingIds]);

    const poll = async () => {
      // Read fresh orthos from store each poll cycle
      const currentOrthos = useProjectStore.getState().data.orthophotos;
      const currentSiteId = useProjectStore.getState().meta.siteId;
      if (!currentSiteId) return;

      const stillPending = currentOrthos.filter(needsPolling);

      if (stillPending.length === 0) {
        // All done — stop polling
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        pollingIdsRef.current.clear();
        console.log('[TilePoller] All tilesets ready, stopping poll');
        return;
      }

      for (const ortho of stillPending) {
        try {
          const status = await getTilesetStatus(currentSiteId, ortho.tilesetId!);
          unknownStatusFailures.delete(ortho.tilesetId!); // reachable again
          console.log(`[TilePoller] ${ortho.name} (${ortho.tilesetId}): status=${status.status}`);
          if (status.status === 'ready' || status.status === 'error') {
            // FAILED REGEN → ROLL BACK. A regen carries priorTilesetId (the
            // tileset it replaces); the server deletes the prior only on a
            // VERIFIED cog_ready, so on error the prior still exists and still
            // serves. Restore it instead of stranding the ortho on a broken
            // new tileset, and discard the failed new one (best-effort — its
            // row also carries the error for diagnostics until deleted).
            if (status.status === 'error' && status.priorTilesetId) {
              const failedNewId = ortho.tilesetId!;
              try {
                const prior = await getTilesetStatus(currentSiteId, status.priorTilesetId);
                if (prior.status === 'ready') {
                  useProjectStore.getState().updateOrthophoto(ortho.id, {
                    tilesetId: prior.id,
                    tileStatus: 'ready',
                    tileMinZoom: prior.minZoom,
                    tileMaxZoom: prior.maxZoom,
                  });
                  // The FAILED tileset row is deliberately NOT deleted here: if
                  // this rollback PATCH loses a _version race (412 → conflict
                  // reload wipes it), the unknown-status self-heal re-polls the
                  // failed id and retries the rollback — which needs the row
                  // (and its priorTilesetId) to still exist. The error row is
                  // inert (terminal, unclaimable by the regen unique-index) and
                  // carries the failure diagnostics until swept.
                  rollbackFailures.delete(failedNewId);
                  console.warn(
                    `[TilePoller] ${ortho.name}: regen failed (${status.error ?? 'unknown'}); ` +
                    `rolled back to previous HD tileset ${prior.id}`
                  );
                  continue;
                }
                // Prior exists but is not ready (was itself errored/mid-gen) —
                // rollback can't help; fall through to the plain error flip.
              } catch {
                // TRANSIENT (network/5xx): keep the ortho 'generating' so the
                // next poll retries the rollback, up to the cap.
                const n = (rollbackFailures.get(failedNewId) ?? 0) + 1;
                rollbackFailures.set(failedNewId, n);
                if (n < MAX_ROLLBACK_FAILURES) continue;
                // Cap reached — fall through to the plain error flip.
              }
            }
            const updates: Partial<Orthophoto> = {
              tileStatus: status.status as 'ready' | 'error',
              tileMinZoom: status.minZoom,
              tileMaxZoom: status.maxZoom,
            };
            // Swap a placeholder/session-bound url for the converter-written
            // COG preview. Folded into the SAME update as the ready flip — a
            // second back-to-back updateOrthophoto would race _version (412 →
            // conflict reload wipes the flip; see the header comment).
            if (status.status === 'ready'
                && (ortho.url.startsWith('data:') || ortho.url.startsWith('blob:'))) {
              const previewUrl = await probeConverterPreview(currentSiteId, ortho.id);
              if (previewUrl) updates.url = previewUrl;
            }
            useProjectStore.getState().updateOrthophoto(ortho.id, updates);
            console.log(
              `[TilePoller] ${ortho.name}: → ${status.status}` +
              (status.status === 'ready' ? ` (${status.tileCount} tiles, z${status.minZoom}-${status.maxZoom})` : '')
            );
          } else if (ortho.url.startsWith('data:') || ortho.url.startsWith('blob:')) {
            // Still generating (large upload → placeholder). The converter writes
            // its preview at the END of the job (the early in-flight preview was
            // removed in converter p2b1-12), so during a convert this probe 404s
            // cheaply. It stays as a SELF-HEAL: if the ready-flip's preview
            // adoption is lost to a 412 conflict rewrite, a later poll adopts it
            // here instead of leaving the placeholder box forever. url-only
            // update; tileStatus stays 'generating' so this ortho keeps polling
            // and the tiles take over on the ready flip. Once adopted, url is no
            // longer data:/blob: so this branch won't re-fire.
            const previewUrl = await probeConverterPreview(currentSiteId, ortho.id);
            if (previewUrl) {
              useProjectStore.getState().updateOrthophoto(ortho.id, { url: previewUrl });
              console.log(`[TilePoller] ${ortho.name}: adopted early preview while generating`);
            }
          }
        } catch (err) {
          const msg = (err as Error).message || '';
          console.warn('[TilePoller] Poll failed:', msg);
          if (ortho.tileStatus == null && ortho.tilesetId) {
            unknownStatusFailures.set(
              ortho.tilesetId, (unknownStatusFailures.get(ortho.tilesetId) ?? 0) + 1);
          }
          // If tileset was deleted or not found: a job we watched generate is an
          // error (user can regenerate); an unknown-status ortho (stale
          // tilesetId from an old project) just reverts to its plain image —
          // don't surface an error badge for something the user never started.
          if (msg.includes('404') || msg.includes('not found') || msg.includes('Not Found')) {
            if (ortho.tileStatus === 'generating') {
              useProjectStore.getState().updateOrthophoto(ortho.id, { tileStatus: 'error' });
              console.warn(`[TilePoller] ${ortho.name}: tileset not found, marked as error`);
            } else {
              useProjectStore.getState().updateOrthophoto(ortho.id, { tilesetId: undefined, tileStatus: undefined });
              console.warn(`[TilePoller] ${ortho.name}: stale tilesetId cleared (tileset gone)`);
            }
          }
        }
      }
    };

    poll();
    timerRef.current = setInterval(poll, POLL_INTERVAL);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [siteId, orthophotos]);
}
